import React, { Dispatch, SetStateAction, useState, useEffect } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import 'dayjs/locale/ja'
import 'react-dates/initialize'
import { Button } from '@/components/atoms/button'
import { ReservationStatusType } from '@/models/reservation-reservation-status'
import { EntireSearchContentType } from '@/models/entire-search'
import { ApprovedStatusType } from '@/models/reservation-approved-status'
import { fetchReservationChannelCode, getAdminGuestRoomType } from '@/apis/aipass'
import { Checkbox } from '@/components/atoms/checkbox'
import { DateRangeField } from '@/components/molecules/date-range-field'
import { InputField } from '@/components/molecules/input-field'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'

type ContainerProps = {
  search: () => void
  clearEntireSearchPanel: () => void
  entireSearchContent: EntireSearchContentType
  setEntireSearchContent: Dispatch<SetStateAction<EntireSearchContentType>>
}

export const EntireSearchPanel: React.FC<ContainerProps> = props => {
  const { t } = useTranslation()
  const [reservationChannelCodes, setReservationChannelCodes] = useState<any>([])
  const [guestRoomTypeMaster, setGuestRoomTypeMaster] = useState<Array<{ id: string; typeNameJa: string }>>([])

  const handleContentChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    console.log({ call: 'handleContentChange', name, value })
    props.setEntireSearchContent({ ...props.entireSearchContent, [name]: value })
  }

  const reservationStatusesChange = (value: ReservationStatusType): void => {
    let reservationStatuses = props.entireSearchContent.reservationStatuses
    if (reservationStatuses.includes(value)) {
      reservationStatuses = reservationStatuses.filter(v => v !== value)
    } else {
      reservationStatuses.push(value)
    }
    props.setEntireSearchContent({ ...props.entireSearchContent, reservationStatuses })
  }

  const approvedStatusesChange = (value: ApprovedStatusType) => {
    let approvedStatuses = props.entireSearchContent.approvedStatuses
    if (approvedStatuses.includes(value)) {
      approvedStatuses = approvedStatuses.filter(v => v !== value)
    } else {
      approvedStatuses.push(value)
    }
    props.setEntireSearchContent({ ...props.entireSearchContent, approvedStatuses })
  }

  useEffect(() => {
    fetchReservationChannelCode().then(setReservationChannelCodes)
    getAdminGuestRoomType().then(res => {
      if (res) setGuestRoomTypeMaster(res)
    })
  }, [])

  return (
    <div css={dropDownSearchPanelStyle}>
      <div className="input-group-block">
        <div className="input-field">
          <div className="title">{t('Full name')}</div>
          <div className="input">
            <InputField
              placeholder={t('John Doe')}
              name="name"
              value={props.entireSearchContent.name}
              onChange={handleContentChange}
              marginBottom={0}
            />
          </div>
        </div>
        <div className="input-field">
          <div className="title">{t('Furigana')}</div>
          <div className="input">
            <InputField
              placeholder="ヤマダ タロウ"
              name="nameKana"
              value={props.entireSearchContent.nameKana}
              onChange={handleContentChange}
              marginBottom={0}
            />
          </div>
        </div>
        <div className="input-field">
          <div className="title">{t('Phone number')}</div>
          <div className="input">
            <InputField
              type="tel"
              placeholder="09012345678"
              name="telephone"
              value={props.entireSearchContent.telephone}
              onChange={handleContentChange}
              marginBottom={0}
            />
          </div>
        </div>
        <div className="input-field">
          <div className="title">{t('Reservation ID')}</div>
          <div className="input">
            <InputField
              placeholder="12345678"
              name="searchReservationId"
              value={props.entireSearchContent.searchReservationId}
              onChange={handleContentChange}
              marginBottom={0}
            />
          </div>
        </div>
      </div>
      <div className="input-group-block">
        <div className="input-field">
          <div className="title">{t('Check-in date')}</div>
          <div className="input">
            <DateRangeField
              beginDate={props.entireSearchContent.checkinDateRange?.from}
              endDate={props.entireSearchContent.checkinDateRange?.to}
              changeRangeDate={(beginDate: string, endDate: string) => {
                props.setEntireSearchContent({
                  ...props.entireSearchContent,
                  checkinDateRange: { from: beginDate, to: endDate },
                })
              }}
            />
          </div>
        </div>
        <div className="input-field">
          <div className="title">{t('Check-out date')}</div>
          <div className="input">
            <DateRangeField
              beginDate={props.entireSearchContent.checkoutDateRange?.from}
              endDate={props.entireSearchContent.checkoutDateRange?.to}
              changeRangeDate={(beginDate: string, endDate: string) => {
                props.setEntireSearchContent({
                  ...props.entireSearchContent,
                  checkoutDateRange: { from: beginDate, to: endDate },
                })
              }}
            />
          </div>
        </div>
        <div className="input-field">
          <div className="title">{t('Date of stay')}</div>
          <div className="input">
            <DateRangeField
              beginDate={props.entireSearchContent.stayDateRange?.from}
              endDate={props.entireSearchContent.stayDateRange?.to}
              changeRangeDate={(beginDate: string, endDate: string) => {
                props.setEntireSearchContent({
                  ...props.entireSearchContent,
                  stayDateRange: { from: beginDate, to: endDate },
                })
              }}
            />
          </div>
        </div>
        <div className="input-field">
          <div className="title">{t('Reservation date')}</div>
          <div className="input">
            <DateRangeField
              beginDate={props.entireSearchContent.reserveDateRange?.from}
              endDate={props.entireSearchContent.reserveDateRange?.to}
              changeRangeDate={(beginDate: string, endDate: string) => {
                props.setEntireSearchContent({
                  ...props.entireSearchContent,
                  reserveDateRange: { from: beginDate, to: endDate },
                })
              }}
            />
          </div>
        </div>
      </div>
      <div className="input-group-block">
        <div className="input-field">
          <div className="title">{t('Channel')}</div>
          <div className="input">
            <Select
              value={props.entireSearchContent.channelCodeName}
              onChange={v => handleContentChange({ target: { value: v.toString(), name: 'channelCodeName' } } as any)}
              customStyle={css({ paddingBottom: 0 })}
            >
              <AntSelect.Option value="all">{t('All')}</AntSelect.Option>
              <AntSelect.Option value="-">{t('-')}</AntSelect.Option>
              {reservationChannelCodes.map(master => (
                <AntSelect.Option key={master.id} value={master.name}>
                  {master.name}
                </AntSelect.Option>
              ))}
            </Select>
          </div>
        </div>
        <div className="input-field">
          <div className="title">{t('Room type')}</div>
          <div className="input">
            <Select
              value={props.entireSearchContent.guestRoomTypeId}
              onChange={v => {
                props.setEntireSearchContent({
                  ...props.entireSearchContent,
                  guestRoomTypeId: v.toString(),
                  guestRoomTypeName: guestRoomTypeMaster.find(m => m.id === v)?.typeNameJa,
                })
              }}
              customStyle={css({ paddingBottom: 0 })}
            >
              <AntSelect.Option value="all">{t('All')}</AntSelect.Option>
              {guestRoomTypeMaster.map(master => (
                <AntSelect.Option key={master.id} value={master.id}>
                  {master.typeNameJa}
                </AntSelect.Option>
              ))}
            </Select>
          </div>
        </div>
        <div className="input-field">
          <div className="title">{t('Room assignment status')}</div>
          <div className="input">
            <Select
              value={props.entireSearchContent.selectGuestRoomAssign}
              onChange={v => handleContentChange({ target: { value: v.toString(), name: 'selectGuestRoomAssign' } } as any)}
              customStyle={css({ paddingBottom: 0 })}
            >
              <AntSelect.Option value="all">{t('All')}</AntSelect.Option>
              <AntSelect.Option value="assigned">{t('Assigned')}</AntSelect.Option>
              <AntSelect.Option value="notAssigned">{t('Not assigned')}</AntSelect.Option>
            </Select>
          </div>
        </div>
        <div className="input-field">
          <div className="title">{t('Room number')}</div>
          <div className="input">
            <InputField
              placeholder="0101"
              name="roomNumber"
              value={props.entireSearchContent.roomNumber}
              onChange={handleContentChange}
              marginBottom={0}
            />
          </div>
        </div>
      </div>
      <div className="input-group-block -double">
        <div className="input-field">
          <div className="title">{t('ReservationNote')}</div>
          <div className="input">
            <InputField
              placeholder={t('Excluding breakfast')}
              name="note"
              value={props.entireSearchContent.note}
              onChange={handleContentChange}
              marginBottom={0}
            />
          </div>
        </div>
      </div>
      <div className="input-group-block">
        <div className="input-field">
          <div className="title">{t('Booking status')}</div>
          <div className="checkbox-grid">
            <Checkbox
              value={props.entireSearchContent.reservationStatuses.includes(ReservationStatusType.NewReport)}
              onChange={() => reservationStatusesChange(ReservationStatusType.NewReport)}
              label={t('New')}
              style={checkBoxOverrideStyle}
            />
            <Checkbox
              value={props.entireSearchContent.reservationStatuses.includes(ReservationStatusType.ModificationReport)}
              onChange={() => reservationStatusesChange(ReservationStatusType.ModificationReport)}
              label={t('Update')}
              style={checkBoxOverrideStyle}
            />
            <Checkbox
              value={props.entireSearchContent.reservationStatuses.includes(ReservationStatusType.CancellationReport)}
              onChange={() => reservationStatusesChange(ReservationStatusType.CancellationReport)}
              label={t('Cancel')}
              style={checkBoxOverrideStyle}
            />
            <Checkbox
              value={props.entireSearchContent.reservationStatuses.includes(ReservationStatusType.NoshowReport)}
              onChange={() => reservationStatusesChange(ReservationStatusType.NoshowReport)}
              label={t('No show')}
              style={checkBoxOverrideStyle}
            />
          </div>
        </div>
        <div className="input-field">
          <div className="title">{t('Accommodation management status')}</div>
          <div className="checkbox-grid">
            <Checkbox
              value={props.entireSearchContent.approvedStatuses.includes(ApprovedStatusType.Reservation)}
              onChange={() => approvedStatusesChange(ApprovedStatusType.Reservation)}
              label={t('Reserve')}
              style={checkBoxOverrideStyle}
            />
            <Checkbox
              value={props.entireSearchContent.approvedStatuses.includes(ApprovedStatusType.Checkin)}
              onChange={() => approvedStatusesChange(ApprovedStatusType.Checkin)}
              label={t('Checkin')}
              style={checkBoxOverrideStyle}
            />
            <Checkbox
              value={props.entireSearchContent.approvedStatuses.includes(ApprovedStatusType.Stay)}
              onChange={() => approvedStatusesChange(ApprovedStatusType.Stay)}
              label={t('During your stay')}
              style={checkBoxOverrideStyle}
            />
            <Checkbox
              value={props.entireSearchContent.approvedStatuses.includes(ApprovedStatusType.Checkout)}
              onChange={() => approvedStatusesChange(ApprovedStatusType.Checkout)}
              label={t('Checkout')}
              style={checkBoxOverrideStyle}
            />
          </div>
        </div>
      </div>
      <div className="panel-footer-block">
        <Button width={92} height={32} buttonType={3} fontSize={12} onClick={props.clearEntireSearchPanel}>
          {t('Clear')}
        </Button>
        <Button width={93} height={32} buttonType={1} fontSize={12} onClick={props.search}>
          {t('Search')}
        </Button>
      </div>
    </div>
  )
}

const checkBoxOverrideStyle = css({ img: { width: 20, height: 20, p: { fontSize: 14 } } })

const dropDownSearchPanelStyle = css({
  width: '100%',
  zIndex: 50,
  backgroundColor: '#FFF',
  position: 'absolute',
  top: 50,
  left: 0,
  border: '1px solid #f2f2f2',
  boxShadow: '0px 3px 4px #0000001a',
  borderRadius: 2,
  padding: 16,
  '.input-group-block': {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, 292px)',
    '&.-double': { gridTemplateColumns: `repeat(auto-fit, ${292 * 2 + 16}px)` },
    gap: 16,
    '@media(max-width: 1080px)': { gap: '16px 8px' },
    paddingTop: 24,
    '&:first-of-type': { paddingTop: 0 },
    '.input-field': {
      '.title': {
        fontSize: 12,
        fontWeight: 'bold',
        color: 'rgb(103, 103, 103)',
        marginBottom: 12,
      },
      '.checkbox-grid': {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '16px 0',
      },
    },
  },
  '.panel-footer-block': {
    display: 'flex',
    paddingTop: 16,
    gap: 16,
  },
})
